"use client";

import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch } from "@/store";
import { fetchAdminFinanceDashboard } from "@/store/modules/adminFinanceDashboard/thunks";
import { clearAdminFinanceDashboard } from "@/store/modules/adminFinanceDashboard/slice";

type GroupBy = "day" | "week" | "month";

export default function useAdminFinanceDashboard() {
  const dispatch: AppDispatch = useDispatch();
  const { data, loading, error } = useSelector(
    (state: any) => state.adminFinanceDashboard,
  );

  const [groupBy, setGroupBy] = useState<GroupBy>("month");
  const today = useMemo(() => new Date(), []);
  const firstDayOfMonth = useMemo(
    () => new Date(today.getFullYear(), today.getMonth(), 1),
    [today],
  );
  const [startDate, setStartDate] = useState<Date | null>(firstDayOfMonth);
  const [endDate, setEndDate] = useState<Date | null>(today);
  const [pageTx, setPageTx] = useState(1);
  const [pagePay, setPagePay] = useState(1);
  const perPageTx = 50;
  const perPagePay = 50;

  const buildParams = useCallback(() => {
    const p: Record<string, any> = {
      page_tx: pageTx,
      per_page_tx: perPageTx,
      page_pay: pagePay,
      per_page_pay: perPagePay,
    };
    if (startDate) p.start_date = startDate.toISOString().slice(0, 10);
    if (endDate) p.end_date = endDate.toISOString().slice(0, 10);
    return p;
  }, [groupBy, pageTx, perPageTx, pagePay, perPagePay, startDate, endDate]);

  const fetch = useCallback(async () => {
    try {
      await dispatch(fetchAdminFinanceDashboard(buildParams())).unwrap();
    } catch (e) {
      // On error, clear to avoid stale UI
      dispatch(clearAdminFinanceDashboard());
    }
  }, [dispatch, buildParams]);

  useEffect(() => {
    fetch();
  }, [fetch]);

  const handleApplyFilters = (next: {
    start?: Date | null;
    end?: Date | null;
    groupBy?: GroupBy;
  }) => {
    if (typeof next.groupBy !== "undefined") setGroupBy(next.groupBy);
    if ("start" in next) setStartDate(next.start ?? null);
    if ("end" in next) setEndDate(next.end ?? null);
    setPageTx(1);
    setPagePay(1);
  };

  const txPagination = useMemo(() => {
    const pagination = data?.lists?.asaas_transactions?.pagination || {};
    const total = pagination.total || 0;
    const per = pagination.per_page || pagination.perPage || perPageTx;
    const current = pagination.page || pagination.current_page || pageTx || 1;
    const totalPages =
      pagination.total_pages ||
      pagination.last_page ||
      Math.max(1, Math.ceil(total / per));
    return { total, per, current, totalPages };
  }, [data, pageTx, perPageTx]);

  const payPagination = useMemo(() => {
    const pagination = data?.lists?.payments?.pagination || {};
    const total = pagination.total || 0;
    const per = pagination.per_page || pagination.perPage || perPagePay;
    const current = pagination.page || pagination.current_page || pagePay || 1;
    const totalPages =
      pagination.total_pages ||
      pagination.last_page ||
      Math.max(1, Math.ceil(total / per));
    return { total, per, current, totalPages };
  }, [data, pagePay, perPagePay]);

  return {
    data,
    loading,
    error,
    groupBy,
    startDate,
    endDate,
    setStartDate,
    setEndDate,
    setGroupBy,
    pageTx,
    setPageTx,
    pagePay,
    setPagePay,
    txPagination,
    payPagination,
    fetch,
    handleApplyFilters,
  };
}
